import { Footer, Header } from '@components';
import { VideoLibraryItemProps } from '../video-item-props';
import { NavLink } from '@remix-run/react';
import React, { useEffect, useState } from 'react';
import { InfoIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import './video-item.css';
import VideoPlayer from '../video-player';
import InputCommentComponent from './input-comment.component';
import CommentComponent from './comment-desktop.component';
import { BrowserCustomerClient } from '@client';
import CommentsLoginPopUp from './comments-login-pop-up';
import SubscribePopup from './subscribe-popup';
import { isNullOrEmpty, mixpanelService } from '@utilities';

const VideoItemDesktop: React.FC<VideoLibraryItemProps> = ({ comments, headerProps, isMobile, isLiked, likes, selectedVideo, suggestedVideos }) => {
  const [frameworkInfo, setFrameworkInfo] = useState<boolean>(false);
  const [isResourceCommentSectionOpen, setIsResourceCommentSectionOpen] = useState<boolean>(false);
  const [isSelfLiked, setIsSelfLiked] = useState<boolean>(false);
  const [noOfLikes, setNoOfLikes] = useState<number>(0);
  const [showLoginPopup, setShowLoginPopup] = useState<boolean>(false);
  const [showSubscribe, setShowSubscribe] = useState<boolean>(false);

  const [viewAllComments, setViewAllComments] = useState<boolean>(false);
  const [showShareNotification, setShowShareNotification] = useState<boolean>(false);

  useEffect(() => {
    setIsSelfLiked(isLiked);
    setNoOfLikes(likes.noOfLikes);
  }, [isLiked, likes]);

  const splittedInfo = selectedVideo?.solutionDescription?.split(',');
  let formattedInfo = splittedInfo ? splittedInfo[0] : '';
  if (splittedInfo && splittedInfo?.length > 1) {
    formattedInfo = `<ul style="list-style-type:none;">`;
    splittedInfo?.map((info, index) => {
      const trimmedInfo = info.trim();
      if (index > 0) {
        formattedInfo = formattedInfo + `<li class="mt-1"> <span class="font-bold"> ${trimmedInfo.charAt(0)}</span>${trimmedInfo.substring(1)} </li>`;
      }
    });
    formattedInfo = formattedInfo + '</ul>';
  }

  const onResourceLike = async () => {
    let userStatus = 'visitor';
    if (headerProps.isLoggedIn) userStatus = 'registered';
    if (headerProps.isSubscribed) userStatus = 'subscriber';
    mixpanelService.track('video_like_click', {
      video_name: selectedVideo?.header,
      tag_name: !isNullOrEmpty(selectedVideo?.resultCategory) ? selectedVideo?.resultCategory : selectedVideo?.tags[0],
      user_status: userStatus,
    });

    if (!headerProps.isLoggedIn) {
      setShowLoginPopup(true);
      return;
    }

    if (!headerProps.isSubscribed && selectedVideo?.knowledgeHubVideoType == 'solution') {
      setShowSubscribe(true);
      return;
    }

    setIsSelfLiked(!isSelfLiked);
    const client = new BrowserCustomerClient();
    try {
      const resp = await client.SaveResourceLike({
        resourceId: selectedVideo?.id ?? '',
      });
      if (resp?.success) {
        const likesResp = await client.GetResourceLikes(selectedVideo?.id ?? '');
        likesResp.data.noOfLikes;
        setIsSelfLiked(!isSelfLiked);
        setNoOfLikes(likesResp.data.noOfLikes);
      }
    } catch (e) {
      console.log(e);
    }

    mixpanelService.track('video_like', {
      video_name: selectedVideo?.header,
      tag_name: !isNullOrEmpty(selectedVideo?.resultCategory) ? selectedVideo?.resultCategory : selectedVideo?.tags[0],
      user_status: userStatus,
    });
  };

  const onCommentClick = () => {
    let userStatus = 'visitor';
    if (headerProps.isLoggedIn) userStatus = 'registered';
    if (headerProps.isSubscribed) userStatus = 'subscriber';
    mixpanelService.track('video_comment_click', {
      video_name: selectedVideo?.header,
      tag_name: !isNullOrEmpty(selectedVideo?.resultCategory) ? selectedVideo?.resultCategory : selectedVideo?.tags[0],
      user_status: userStatus,
    });
    if (!headerProps.isLoggedIn) {
      setShowLoginPopup(true);
      return;
    }

    if (!headerProps.isSubscribed && selectedVideo?.knowledgeHubVideoType == 'solution') {
      setShowSubscribe(true);
      return;
    }

    setIsResourceCommentSectionOpen(!isResourceCommentSectionOpen);
  };

  const onShareClick = () => {
    let userStatus = 'visitor';
    if (headerProps.isLoggedIn) userStatus = 'registered';
    if (headerProps.isSubscribed) userStatus = 'subscriber';
    mixpanelService.track('video_share_click', {
      video_name: selectedVideo?.header,
      tag_name: !isNullOrEmpty(selectedVideo?.resultCategory) ? selectedVideo?.resultCategory : selectedVideo?.tags[0],
      user_status: userStatus,
    });
    const pageLink = window.location.href;
    // navigator.clipboard.writeText(pageLink); // Copy the link to clipboard

    if (navigator?.clipboard) {
      navigator.clipboard.writeText(pageLink).catch(err => {
        console.error('Failed to copy coupon code:', err);
      });
    } else {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = pageLink;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Failed to copy coupon code:', err);
      }
      document.body.removeChild(textArea);
    }

    mixpanelService.track('video_share', {
      video_name: selectedVideo?.header,
      tag_name: !isNullOrEmpty(selectedVideo?.resultCategory) ? selectedVideo?.resultCategory : selectedVideo?.tags[0],
      user_status: userStatus,
    });

    setShowShareNotification(true); // Show the popup
    setTimeout(() => setShowShareNotification(false), 3000);
  };

  const onVideoComplete = () => {
    if (selectedVideo?.knowledgeHubVideoType === 'solution' && !headerProps.isSubscribed) {
      setShowSubscribe(true);
    }
  };

  const onVideoStarted = () => {
    if (!selectedVideo) return;
    let userStatus = 'visitor';
    if (headerProps.isLoggedIn) userStatus = 'registered';
    if (headerProps.isSubscribed ?? false) userStatus = 'subscriber';
    mixpanelService.track('video_watch', {
      video_name: selectedVideo.header,
      tag_name: !isNullOrEmpty(selectedVideo.resultCategory) ? selectedVideo.resultCategory : selectedVideo.tags[0],
      user_status: userStatus,
    });
  };

  let displaySuggestedVideos = suggestedVideos.slice(0, 3);

  if (headerProps.isSubscribed) {
    displaySuggestedVideos = suggestedVideos;
  }

  return (
    <div className="flex flex-col gap-y-0 relative">
      <Header headerProps={headerProps} />

      <CommentsLoginPopUp open={showLoginPopup} onClose={() => setShowLoginPopup(false)} />
      <SubscribePopup open={showSubscribe} onClose={() => setShowSubscribe(false)} />
      {showShareNotification && (
        <div className="pop-section">
          <div className="content">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <g clipPath="url(#clip0_3692_27231)">
                <circle cx="12" cy="12" r="10" fill="#165DA1" />
                <path
                  d="M10.3625 15.6075L9.3815 16.5599C9.12426 16.8168 8.77535 16.9612 8.41155 16.9612C8.04774 16.9612 7.69884 16.8168 7.44159 16.5599C7.18434 16.3029 7.03982 15.9543 7.03982 15.5909C7.03982 15.2274 7.18434 14.8789 7.44159 14.6219L9.95793 12.1025C10.2049 11.855 10.5377 11.7118 10.8874 11.7025C11.2372 11.6932 11.5771 11.8186 11.8369 12.0527L11.9034 12.1081C12.0085 12.2109 12.1502 12.2677 12.2972 12.2662C12.4443 12.2646 12.5848 12.2047 12.6877 12.0997C12.7906 11.9947 12.8475 11.8532 12.8459 11.7063C12.8444 11.5593 12.7845 11.4191 12.6794 11.3163C12.6479 11.276 12.6146 11.2372 12.5796 11.2C12.1064 10.7887 11.4945 10.5725 10.8678 10.5952C10.2411 10.6178 9.64635 10.8775 9.20414 11.3218L6.65454 13.8412C6.22131 14.3122 5.987 14.9323 6.00056 15.5719C6.01411 16.2115 6.27448 16.8211 6.72728 17.2734C7.18008 17.7258 7.79031 17.9859 8.43053 17.9994C9.07075 18.013 9.69145 17.7789 10.163 17.3461L11.1219 16.4103C11.2166 16.3072 11.2686 16.172 11.2675 16.032C11.2665 15.8921 11.2124 15.7577 11.1162 15.6559C11.02 15.5542 10.8887 15.4926 10.7489 15.4836C10.6091 15.4746 10.471 15.5189 10.3625 15.6075ZM16.2876 7.7227C15.8213 7.25981 15.1907 7 14.5334 7C13.876 7 13.2454 7.25981 12.7791 7.7227L11.8203 8.65847C11.7256 8.76165 11.6735 8.89683 11.6746 9.0368C11.6756 9.17676 11.7297 9.31114 11.826 9.41288C11.9222 9.51463 12.0534 9.57619 12.1932 9.58518C12.3331 9.59417 12.4711 9.54992 12.5796 9.46134L13.5385 8.50897C13.7957 8.25198 14.1446 8.1076 14.5084 8.1076C14.8722 8.1076 15.2211 8.25198 15.4784 8.50897C15.7356 8.76596 15.8801 9.11451 15.8801 9.47795C15.8801 9.84139 15.7356 10.1899 15.4784 10.4469L12.962 12.9663C12.715 13.2138 12.3823 13.3571 12.0325 13.3663C11.6828 13.3756 11.3429 13.2502 11.0831 13.0161L11.0166 12.9608C10.9115 12.858 10.7698 12.8011 10.6227 12.8026C10.4756 12.8042 10.3352 12.8641 10.2323 12.9691C10.1294 13.0741 10.0725 13.2156 10.074 13.3625C10.0756 13.5095 10.1355 13.6498 10.2406 13.7526C10.2811 13.7935 10.3236 13.8323 10.3681 13.8688C10.8418 14.2788 11.4535 14.4942 12.0798 14.4716C12.7062 14.449 13.3007 14.1901 13.7435 13.747L16.2654 11.2277C16.7317 10.7648 16.9958 10.1364 17 9.47975C17.0041 8.82307 16.748 8.19141 16.2876 7.7227Z"
                  fill="#AFBFCF"
                />
              </g>
              <defs>
                <clipPath id="clip0_3692_27231">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </div>
          <div className="para">
            <p className="text">Link copied to clipboard!</p>
          </div>
        </div>
      )}

      <div className="py-10 px-28 flex flex-col gap-y-10">
        {/* Breadcrumb  */}
        <div className="flex gap-x-[10px] w-[534px] overflow-hidden whitespace-nowrap">
          <NavLink to="/" className="font-medium text-[20px]/[100%]" reloadDocument>
            Home
          </NavLink>
          <span className="font-medium text-[20px]/[100%]">/</span>
          <NavLink to="/knowledge-hub/resources" className="font-medium text-[20px]/[100%]" reloadDocument>
            Knowledge Hub
          </NavLink>
          <span className="font-medium text-[20px]/[100%]">/</span>
          <NavLink to="/knowledge-hub/resources/videos" className="font-medium text-[20px]/[100%]" reloadDocument>
            Videos
          </NavLink>
          <span className="font-medium text-[20px]/[100%]">/</span>
          <div className="font-bold text-[20px]/[100%] underline capitalize truncate">{selectedVideo!.header}</div>
        </div>

        <div className="flex flex-col gap-y-12 w-full">
          {/* Title */}
          <div className="flex flex-col gap-y-4 w-full">
            <h1 className="font-bold text-[36px]/[100%] text-[#1E192B]">{selectedVideo?.header}</h1>
            {selectedVideo?.solutionName && (
              <div className="flex gap-x-4 items-center relative">
                <span className="py-1 px-3 bg-[#DCE7F1] rounded-2xl text-[20px]/[100%] font-medium text-[#004E98]">Framework</span>
                <span className="font-medium text-[20px]/[100%] underline text-[#625B70]">{selectedVideo?.solutionName}</span>
                <div className="text-[#667085]" onMouseOver={() => setFrameworkInfo(true)} onFocus={() => setFrameworkInfo(true)} onMouseLeave={() => setFrameworkInfo(false)}>
                  <InfoIcon className="size-4 text-[#667085]" />
                </div>
                {frameworkInfo && (
                  <div className="flex gap-x-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="9" height="16" viewBox="0 0 9 16" fill="none">
                      <path
                        d="M8.51471 14.0711C8.51471 14.962 7.43757 15.4081 6.8076 14.7782L0.736529 8.7071C0.346004 8.31658 0.346005 7.68342 0.736529 7.29289L6.8076 1.22183C7.43757 0.591867 8.51471 1.03803 8.51471 1.92894L8.51471 14.0711Z"
                        fill="white"
                      />
                    </svg>
                    <div className="flex gap-0 items-center absolute transform translate-x-[265px] translate-y-[12px] z-10 left-0 -bottom-full">
                      <div className="flex flex-col items-start flex-1 p-3 rounded-lg bg-white w-[320px] shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08),0px_4px_6px_-2px_rgba(16,24,40,0.03)]">
                        <p className="w-full text-[#344054] font-medium text-base leading-5">The {selectedVideo.solutionName} Framework</p>
                        {/* <p className="description"> {result.frameworkInfo} </p> */}
                        <div className="font-medium text-sm leading-5 text-[#667085] w-full" dangerouslySetInnerHTML={{ __html: formattedInfo as string }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Videos, Suggested Videos & Comments and Share Buttons */}
          <div className="flex gap-x-4 w-full">
            {/* Videos, Comments and Share Buttons and Comments Section  */}
            <div className="flex flex-col gap-y-6 w-[70%]">
              <VideoPlayer selectedVideo={selectedVideo} isMobile={isMobile} onVideoComplete={onVideoComplete} onVideoStarted={onVideoStarted} />
              <p className="font-normal text-base leading-6">{selectedVideo?.shortDescription}</p>
              <div className="share-section">
                <button className="input cursor-pointer space-x-6" onClick={() => onResourceLike()}>
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                  <div className="relative inline-block cursor-pointer mb-6">
                    {/* First SVG (Heart) */}
                    <motion.svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      initial={{ scale: 1, opacity: 1 }}
                      animate={{
                        scale: isSelfLiked ? 0.5 : 1, // Shrink on click
                        opacity: isSelfLiked ? 0 : 1, // Fade out
                        rotate: isSelfLiked ? 360 : 0, // Rotate
                      }}
                      transition={{ duration: 0.5 }}
                      className="absolute"
                    >
                      <path
                        d="M12.62 20.81C12.28 20.93 11.72 20.93 11.38 20.81C8.48 19.82 2 15.69 2 8.68998C2 5.59998 4.49 3.09998 7.56 3.09998C9.38 3.09998 10.99 3.97998 12 5.33998C13.01 3.97998 14.63 3.09998 16.44 3.09998C19.51 3.09998 22 5.59998 22 8.68998C22 15.69 15.52 19.82 12.62 20.81Z"
                        stroke="#292D32"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </motion.svg>
                    {/* Second SVG (Filled Heart) */}
                    <motion.svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      initial={{ scale: 0, opacity: 0, rotate: 0 }} // Start hidden
                      animate={{
                        scale: isSelfLiked ? 1 : 0, // Grow to actual size
                        opacity: isSelfLiked ? 1 : 0, // Fade in
                        rotate: isSelfLiked ? 360 : 0, // Rotate
                      }}
                      transition={{ duration: 0.5 }}
                      className="absolute "
                    >
                      <path
                        d="M12.62 20.81C12.28 20.93 11.72 20.93 11.38 20.81C8.48 19.82 2 15.69 2 8.68998C2 5.59998 4.49 3.09998 7.56 3.09998C9.38 3.09998 10.99 3.97998 12 5.33998C13.01 3.97998 14.63 3.09998 16.44 3.09998C19.51 3.09998 22 5.59998 22 8.68998C22 15.69 15.52 19.82 12.62 20.81Z"
                        fill="#EB5050"
                        stroke="#EB5050"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </motion.svg>
                  </div>

                  <p className="text"> {noOfLikes} </p>
                </button>

                <button className="input cursor-pointer" onClick={onCommentClick}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path
                      d="M1 17L2.15466 13.1052C0.0904842 9.67271 0.888087 5.24358 4.01988 2.74489C7.15167 0.247204 11.6495 0.451933 14.5406 3.22426C17.4317 5.99758 17.8225 10.4806 15.4546 13.7114C13.0866 16.9421 8.69092 17.9208 5.17454 16.0013L1 17Z"
                      stroke="#292D32"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="text"> {comments.length ?? 0} </p>
                </button>

                <button className="input cursor-pointer" onClick={onShareClick}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M13.75 11.875L17.5 8.125L13.75 4.375" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path
                      d="M2.5 15.625C2.5 13.6359 3.29018 11.7282 4.6967 10.3217C6.10322 8.91518 8.01088 8.125 10 8.125H17.5"
                      stroke="#292D32"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="text">Share</p>
                </button>
              </div>

              <div className="w-full border-t border-[#E9E5DF] py-4 flex flex-col gap-y-6">
                {comments.length > 0 && <h3 className="font-medium text-[20px]/[100%] text-[#1E192B] tracking-[0%]">Comments</h3>}

                {isResourceCommentSectionOpen && (
                  <InputCommentComponent
                    profileName={headerProps.customerName ?? ''}
                    profilePicture={headerProps.customerImageUrl ?? ''}
                    resourceId={selectedVideo?.id ?? ''}
                    isAuthenticated={headerProps.isLoggedIn}
                    isSubscribed={headerProps.isSubscribed}
                    videoHeader={selectedVideo?.header ?? ''}
                    videoTags={(!isNullOrEmpty(selectedVideo?.resultCategory) ? selectedVideo?.resultCategory : selectedVideo?.tags[0]) ?? ''}
                  />
                )}

                {comments.slice(0, 2).map((comment, index) => {
                  return (
                    <CommentComponent
                      key={index}
                      comment={comment}
                      level={0}
                      resourceId={selectedVideo?.id ?? ''}
                      isAuthenticated={headerProps.isLoggedIn}
                      isSubscribed={headerProps.isSubscribed}
                      profileName={headerProps.customerName ?? ''}
                      profilePicture={headerProps.customerImageUrl ?? ''}
                      knowledgeHubVideoType={selectedVideo?.knowledgeHubVideoType}
                      onNotSubscribed={() => setShowSubscribe(true)}
                      onUnAuthenticated={() => setShowLoginPopup(true)}
                      videoHeader={selectedVideo?.header ?? ''}
                      videoTags={(!isNullOrEmpty(selectedVideo?.resultCategory) ? selectedVideo?.resultCategory : selectedVideo?.tags[0]) ?? ''}
                    />
                  );
                })}

                {viewAllComments &&
                  comments.slice(2).map((comment, index) => {
                    return (
                      <CommentComponent
                        key={index}
                        comment={comment}
                        level={0}
                        resourceId={selectedVideo?.id ?? ''}
                        isAuthenticated={headerProps.isLoggedIn}
                        isSubscribed={headerProps.isSubscribed}
                        profileName={headerProps.customerName ?? ''}
                        profilePicture={headerProps.customerImageUrl ?? ''}
                        knowledgeHubVideoType={selectedVideo?.knowledgeHubVideoType}
                        onNotSubscribed={() => setShowSubscribe(true)}
                        onUnAuthenticated={() => setShowLoginPopup(true)}
                        videoHeader={selectedVideo?.header ?? ''}
                        videoTags={(!isNullOrEmpty(selectedVideo?.resultCategory) ? selectedVideo?.resultCategory : selectedVideo?.tags[0]) ?? ''}
                      />
                    );
                  })}
                {comments.length > 2 && (
                  <button className="view-all" onClick={() => setViewAllComments(!viewAllComments)}>
                    View all comments
                  </button>
                )}
              </div>
            </div>

            {/* Suggested Videos  */}
            <div className="flex flex-col gap-y-4 w-[30%] ">
              <h3 className="font-medium text-[24px]/[34px] tracking-[0.4px] text-[#0B0A0A]">Suggestions</h3>
              {displaySuggestedVideos.map((v, i) => {
                return (
                  <NavLink
                    to={`/videos/${v.slug}`}
                    key={i}
                    className="flex w-full border border-[#E3E8EF] rounded-lg h-[120px]"
                    onClick={() => {
                      let userStatus = 'visitor';
                      if (headerProps.isLoggedIn) userStatus = 'registered';
                      if (headerProps.isSubscribed ?? false) userStatus = 'subscriber';
                      mixpanelService.track('video_open_click', {
                        video_name: v.header,
                        tag_name: !isNullOrEmpty(v.resultCategory) ? v.resultCategory : v.tags[0],
                        user_status: userStatus,
                      });
                    }}
                  >
                    <div className="w-[50%] relative">
                      <img src={v.thumbnailUrl} alt={v.header} className="w-full h-full" />
                      <div className="bg-black text-white rounded-lg px-1 absolute bottom-3 right-3 font-medium text-[12px]/[20px] text-center">
                        {v.duration.startsWith('00') ? v.duration.substring(3) : v.duration}
                      </div>
                    </div>

                    <div className="flex flex-col p-4 gap-y-4 w-[50%]">
                      <p className="font-medium text-[20px]/[100%] text-[#000] line-clamp-2">{v.header}</p>
                      <span className="font-medium text-base/[20px] tracking-[0%] text-[#666] flex items-center">
                        {v.likes} Likes<span className="mx-2 text-2xl">&bull;</span>
                        {v.comments} Comments
                      </span>
                    </div>
                  </NavLink>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <Footer footerCopyright={true} />
    </div>
  );
};

export default VideoItemDesktop;
